import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { sprayService, SprayOperator } from '../../services/sprayService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar } from '../patterns';
import { OperatorLicencePill } from './statusPills';

interface Props {
  onAdd?: () => void;
  onEdit?: (operator: SprayOperator) => void;
  onDelete?: (id: string) => void;
}

const SprayOperatorList: React.FC<Props> = ({ onAdd, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [operators, setOperators] = useState<SprayOperator[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.operators.title') },
  ]);

  useEffect(() => {
    sprayService.getOperators()
      .then(setOperators)
      .catch(() => setError(t('spray.operators.loadError')));
    // Mount-only fetch: t is treated as stable (we only use it for the error message)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('spray.operators.confirmDelete'))) return;
    try {
      await sprayService.deleteOperator(id);
      setError('');
      setOperators(prev => prev.filter(o => o._id !== id));
      onDelete?.(id);
    } catch {
      setError(t('spray.operators.saveError'));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return operators;
    return operators.filter(o =>
      o.name.toLowerCase().includes(q) ||
      (o.licenseNo ?? '').toLowerCase().includes(q)
    );
  }, [operators, search]);

  return (
    <div data-testid="spray-operator-list">
      <PageHeader
        title={t('spray.operators.title')}
        meta={t('spray.operators.metaCount', { count: operators.length, defaultValue: '{{count}} operators' })}
        actions={
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={() => onAdd?.()}
          >
            <i className="bi bi-plus-lg" aria-hidden="true" /> {t('spray.pageHeader.actions.newOperator')}
          </button>
        }
      />

      {error && <Alert variant="danger" role="alert" className="mb-3">{error}</Alert>}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('spray.operators.searchPlaceholder')}
      />

      <DataTable<SprayOperator>
        rows={filtered}
        rowKey={(o) => o._id}
        columns={[
          { key: 'name',     label: t('spray.operators.name') },
          { key: 'licenseNo', label: t('spray.operators.licenseNo') },
          {
            key: 'licenseExpiry',
            label: t('spray.operators.licenseExpiry'),
            render: (o) => <OperatorLicencePill expiry={o.licenseExpiry} />,
          },
          {
            key: 'actions',
            label: '',
            align: 'right',
            render: (o) => (
              <span className="d-inline-flex gap-2">
                <button
                  type="button"
                  className="pat-btn pat-btn--subtle"
                  onClick={() => onEdit?.(o)}
                >
                  {t('common.edit')}
                </button>
                <button
                  type="button"
                  className="pat-btn pat-btn--danger"
                  onClick={() => handleDelete(o._id)}
                >
                  {t('common.delete')}
                </button>
              </span>
            ),
          },
        ]}
        emptyState={<div className="p-4 text-muted">{t('spray.operators.noOperators')}</div>}
      />
    </div>
  );
};

export default SprayOperatorList;
